import React from "react";
import { Box, Flex, Spinner, Text, TextProps } from "@chakra-ui/react";
import Button from "@/shared/components/atoms/Button/Button";
import {
  purchaseCardColors,
  purchaseCardFont,
  purchaseCardText,
  PurchaseCardTextRole,
} from "./purchaseCardTokens";
import { CardPricing, describePricing, ScheduleRow } from "./pricing";

interface CardTextProps extends TextProps {
  role: PurchaseCardTextRole;
}

const CardText: React.FC<CardTextProps> = ({ role, ...rest }) => (
  <Text
    fontFamily={purchaseCardFont}
    {...(purchaseCardText[role] as TextProps)}
    {...rest}
  />
);

interface PurchaseCardProps {
  title: string;
  description: string;
  pricing: CardPricing;
  onAction: () => void;
  isLoading?: boolean;
  isActionLoading?: boolean;
  isActionDisabled?: boolean;
}

const Pill: React.FC<{
  label: string;
  tone: "accent" | "neutral";
  icon: React.ElementType;
}> = ({ label, tone, icon }) => {
  const isAccent = tone === "accent";
  const fg = isAccent ? purchaseCardColors.accent : purchaseCardColors.ink;
  return (
    <Flex
      alignSelf="flex-start"
      alignItems="center"
      gap="8px"
      px="12px"
      height="32px"
      borderRadius="100px"
      bg={
        isAccent
          ? purchaseCardColors.accentSurface
          : purchaseCardColors.pillNeutral
      }
      data-testid="purchase-card-pill"
    >
      <Box as={icon} width="20px" height="20px" flexShrink={0} fill={fg} />
      <CardText role="pretitle" color={fg}>
        {label}
      </CardText>
    </Flex>
  );
};

const subscribeToNothing = () => () => undefined;

// The charge dates fall on the buyer's calendar, which the server cannot know,
// so they are left blank in server HTML and filled in once hydrated.
const useIsHydrated = () =>
  React.useSyncExternalStore(
    subscribeToNothing,
    () => true,
    () => false
  );

const ChargeSchedule: React.FC<{ rows: ScheduleRow[] }> = ({ rows }) => {
  const isHydrated = useIsHydrated();
  return (
    <Flex direction="column" gap="8px">
      <CardText role="pretitle" color={purchaseCardColors.inkMuted}>
        Resumen de cobros
      </CardText>
      <Flex direction="column" gap="12px" data-testid="purchase-card-schedule">
        {rows.map((row, index) => (
          <React.Fragment key={`${row.date}-${row.label}`}>
            {index > 0 && (
              <Box
                height="0"
                borderTopWidth="1px"
                borderColor={purchaseCardColors.divider}
              />
            )}
            <Flex justifyContent="space-between" alignItems="center" gap="4px">
              <Flex direction="column" gap="4px">
                <CardText role="caption" color={purchaseCardColors.inkStrong}>
                  {isHydrated ? row.date : "\u00a0"}
                </CardText>
                <CardText role="feedback" color={purchaseCardColors.inkMuted}>
                  {row.label}
                </CardText>
              </Flex>
              <CardText
                role="caption"
                color={purchaseCardColors.inkStrong}
                textAlign="right"
                whiteSpace="nowrap"
              >
                {row.amount}
              </CardText>
            </Flex>
          </React.Fragment>
        ))}
      </Flex>
    </Flex>
  );
};

export const PurchaseCard: React.FC<PurchaseCardProps> = ({
  title,
  description,
  pricing,
  onAction,
  isLoading = false,
  isActionLoading = false,
  isActionDisabled = false,
}) => {
  const view = describePricing(pricing);

  // The single-line variants carry a 3px bottom nudge on this row in the design
  // file; the two-line variant does not.
  const secondaryLinePaddingBottom =
    view.secondaryLines.length === 1 ? "3px" : undefined;

  const shell = (children: React.ReactNode, gap: string) => (
    <Flex
      direction="column"
      gap={gap}
      w="100%"
      bg={purchaseCardColors.surface}
      boxShadow={`0 0 0 1px ${purchaseCardColors.border}`}
      borderRadius="12px"
      p="24px"
      fontFamily={purchaseCardFont}
      data-testid="purchase-card"
    >
      {children}
    </Flex>
  );

  if (isLoading) {
    return shell(
      <Flex minH="230px" alignItems="center" justifyContent="center">
        <Spinner color={purchaseCardColors.ink} />
      </Flex>,
      "16px"
    );
  }

  const cta = (
    <Button
      variant="primary"
      onClick={onAction}
      isLoading={isActionLoading}
      isDisabled={isActionDisabled}
      w="100%"
      height="48px"
      borderRadius="8px"
      bg={purchaseCardColors.ctaSurface}
      color={purchaseCardColors.ctaInk}
      fontFamily={purchaseCardFont}
      fontSize="14px"
      lineHeight="18px"
      fontWeight={500}
      _hover={{ bg: purchaseCardColors.ctaSurface, opacity: 0.88 }}
      _active={{ bg: purchaseCardColors.ctaSurface, opacity: 0.8 }}
      data-testid="purchase-card-cta"
    >
      {view.ctaLabel}
    </Button>
  );

  const offer = (
    <Flex direction="column">
      <Flex direction="column" gap="12px">
        <Flex direction="column" gap="4px">
          <CardText role="title" color={purchaseCardColors.ink}>
            {title}
          </CardText>
          <CardText role="body" color={purchaseCardColors.inkMuted}>
            {description}
          </CardText>
        </Flex>

        {view.headlineSuffix ? (
          <Flex alignItems="flex-end" gap="4px">
            <CardText
              role="price"
              color={purchaseCardColors.ink}
              data-testid="purchase-card-price"
            >
              {view.headline}
            </CardText>
            <CardText
              role="body"
              color={purchaseCardColors.ink}
              pb="4px"
              whiteSpace="nowrap"
            >
              {view.headlineSuffix}
            </CardText>
          </Flex>
        ) : (
          <Flex direction="column" gap="4px">
            <CardText
              role="price"
              color={purchaseCardColors.ink}
              data-testid="purchase-card-price"
            >
              {view.headline}
            </CardText>
            {view.secondaryLines.map((line) => (
              <CardText
                key={line}
                role="body"
                color={purchaseCardColors.ink}
                pb={secondaryLinePaddingBottom}
              >
                {line}
              </CardText>
            ))}
          </Flex>
        )}
      </Flex>

      {view.inlineNote && (
        <CardText role="feedback" color={purchaseCardColors.inkMuted}>
          {view.inlineNote}
        </CardText>
      )}
    </Flex>
  );

  const disclaimer = view.disclaimer && (
    <CardText
      role="feedback"
      color={purchaseCardColors.inkMuted}
      data-testid="purchase-card-disclaimer"
    >
      {view.disclaimer}
    </CardText>
  );

  if (view.schedule) {
    return shell(
      <>
        <Flex direction="column" gap="16px">
          <Pill {...view.pill} />
          {offer}
          {cta}
        </Flex>
        <Flex direction="column" gap="16px">
          <ChargeSchedule rows={view.schedule} />
          {disclaimer}
        </Flex>
      </>,
      "24px"
    );
  }

  return shell(
    <>
      <Pill {...view.pill} />
      {offer}
      <Flex direction="column" gap="4px">
        {cta}
        {disclaimer}
      </Flex>
    </>,
    "16px"
  );
};
